import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

const SECTIONS = [
  { id: "top", label: "Hero", dark: false },
  { id: "ribbon-series", label: "Ribbon Series", dark: false },
  { id: "works", label: "Works", dark: false },
  { id: "philosophy", label: "Studio Note", dark: true },
  { id: "words", label: "Words", dark: false },
];

export default function SectionIndicator() {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const els = SECTIONS.map((s) => document.getElementById(s.id)).filter(Boolean);
    if (els.length === 0) return;

    const io = new IntersectionObserver(
      (entries) => {
        // pick the entry whose center is closest to viewport center among intersecting ones
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length === 0) return;
        const vh = window.innerHeight;
        let best = visible[0];
        let bestDist = Infinity;
        for (const e of visible) {
          const rect = e.target.getBoundingClientRect();
          const center = rect.top + rect.height / 2;
          const dist = Math.abs(center - vh / 2);
          if (dist < bestDist) {
            bestDist = dist;
            best = e;
          }
        }
        const idx = SECTIONS.findIndex((s) => s.id === best.target.id);
        if (idx !== -1) setActive(idx);
      },
      { threshold: [0, 0.25, 0.5, 0.75, 1], rootMargin: "-25% 0px -25% 0px" }
    );

    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  const goTo = (id) => {
    const el = id === "top" ? null : document.getElementById(id);
    if (id === "top") {
      if (window.__lenis) window.__lenis.scrollTo(0);
      else window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    if (el && window.__lenis) window.__lenis.scrollTo(el);
    else if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const num = String(active + 1).padStart(2, "0");
  const total = String(SECTIONS.length).padStart(2, "0");
  const label = SECTIONS[active]?.label || "";
  const isDark = !!SECTIONS[active]?.dark;
  const fg = isDark ? "text-bone/80" : "text-ink/75";
  const fgDim = isDark ? "text-bone/40" : "text-ink/40";
  const fgLabel = isDark ? "text-bone/60" : "text-ink/55";
  const tickIdle = isDark ? "bg-bone/25" : "bg-ink/25";
  const tickHover = isDark ? "hover:bg-bone/70" : "hover:bg-ink/70";

  return (
    <div
      data-testid="section-indicator"
      className="hidden md:flex fixed left-4 top-1/2 -translate-y-1/2 z-[7900] flex-col items-center gap-3 select-none pointer-events-none transition-colors duration-500"
    >
      {/* number — the only piece with weight */}
      <div className={`font-mono text-[10px] tracking-[0.3em] uppercase ${fg} transition-colors duration-500`}>
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={num}
            initial={{ y: 6, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -6, opacity: 0 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="inline-block"
          >
            {num}
          </motion.span>
        </AnimatePresence>
      </div>

      {/* station ticks — each is a quiet, clickable station marker */}
      <div className="flex flex-col items-center gap-2 py-1 pointer-events-auto">
        {SECTIONS.map((s, i) => {
          const isActive = i === active;
          return (
            <button
              key={s.id}
              type="button"
              onClick={() => goTo(s.id)}
              data-testid={`indicator-station-${s.id}`}
              aria-label={`Go to section ${String(i + 1).padStart(2, "0")} — ${s.label}`}
              data-cursor="hover"
              className="group relative flex items-center justify-center w-4 h-2 cursor-pointer"
            >
              <motion.span
                animate={{
                  height: isActive ? 12 : 6,
                  width: 1,
                }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                className={`block transition-colors duration-300 ${
                  isActive ? "bg-crimson" : `${tickIdle} ${tickHover}`
                }`}
              />
            </button>
          );
        })}
      </div>

      {/* total + label, very quiet */}
      <div className={`font-mono text-[9px] tracking-[0.3em] uppercase ${fgDim} transition-colors duration-500`}>
        {total}
      </div>
      <div className={`font-mono text-[9px] tracking-[0.25em] uppercase ${fgLabel} [writing-mode:vertical-rl] rotate-180 transition-colors duration-500`}>
        <AnimatePresence mode="wait" initial={false}>
          <motion.span
            key={label}
            initial={{ y: 8, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -8, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          >
            {label}
          </motion.span>
        </AnimatePresence>
      </div>
    </div>
  );
}
